import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Lock,
  Person,
  SupervisorAccount,
  EditNote
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { adminService } from '../services/api';
import AdminForm from '../components/AdminForm';
import ChangePasswordDialog from '../components/ChangePasswordDialog';

const Admins = () => {
  const { t, i18n } = useTranslation();
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordAdmin, setPasswordAdmin] = useState(null);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, admin: null });
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchAdmins();
  }, [search, roleFilter, statusFilter, page, rowsPerPage]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAll({
        search,
        role: roleFilter,
        isActive: statusFilter,
        page: page + 1,
        limit: rowsPerPage
      });

      setAdmins(response.admins || []);
      setTotalCount(response.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching admins:', error);
      showAlert('error', 'Failed to fetch administrators');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (severity, message) => {
    setAlert({ severity, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleAddAdmin = () => {
    setSelectedAdmin(null);
    setFormOpen(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setFormOpen(true);
  };

  const handleChangePassword = (admin) => {
    setPasswordAdmin(admin);
    setPasswordDialogOpen(true);
  };

  const handleDeleteClick = (admin) => {
    setDeleteDialog({ open: true, admin });
  };

  const handleDeleteConfirm = async () => {
    const adminToDelete = deleteDialog.admin;
    setDeleteDialog({ open: false, admin: null });

    try {
      await adminService.delete(adminToDelete._id || adminToDelete.id);
      showAlert('success', t('admins.adminDeleted'));
      fetchAdmins();
    } catch (error) {
      console.error('Error deleting admin:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete administrator';
      showAlert('error', errorMessage);
    }
  };

  const handleFormSuccess = (message) => {
    setFormOpen(false);
    showAlert('success', message || t('admins.adminCreated'));
    fetchAdmins();
  };

  const handlePasswordSuccess = (message) => {
    setPasswordDialogOpen(false);
    showAlert('success', message || t('admins.passwordChanged'));
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'superadmin': return <SupervisorAccount fontSize="small" />;
      case 'admin': return <Person fontSize="small" />;
      case 'editor': return <EditNote fontSize="small" />;
      default: return <Person fontSize="small" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'superadmin': return 'error';
      case 'admin': return 'primary';
      case 'editor': return 'secondary';
      default: return 'default';
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(
      i18n.language === 'gr' ? 'el-GR' : 'en-US',
      { year: 'numeric', month: 'short', day: 'numeric' }
    );
  };

  const isCurrentAdmin = (admin) => {
    return admin._id === currentAdmin?.id || admin.id === currentAdmin?.id;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('admins.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddAdmin}
        >
          {t('admins.add')}
        </Button>
      </Box>

      {alert && (
        <Alert severity={alert.severity} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            placeholder={t('admins.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flex: '1 1 300px' }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{t('admins.filterByRole')}</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              label={t('admins.filterByRole')}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="superadmin">{t('admins.roles.superadmin')}</MenuItem>
              <MenuItem value="admin">{t('admins.roles.admin')}</MenuItem>
              <MenuItem value="editor">{t('admins.roles.editor')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{t('admins.filterByStatus')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label={t('admins.filterByStatus')}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="true">{t('admins.active')}</MenuItem>
              <MenuItem value="false">{t('admins.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admins.username')}</TableCell>
              <TableCell>{t('admins.email')}</TableCell>
              <TableCell>{t('admins.role')}</TableCell>
              <TableCell>{t('admins.status')}</TableCell>
              <TableCell>{t('admins.lastLogin')}</TableCell>
              <TableCell>{t('admins.createdAt')}</TableCell>
              <TableCell align="right">{t('admins.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary">
                    {t('admins.noAdmins')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin._id || admin.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {admin.username}
                      {isCurrentAdmin(admin) && (
                        <Chip label="You" size="small" color="info" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{admin.email}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getRoleIcon(admin.role)}
                      label={t(`admins.roles.${admin.role}`)}
                      size="small"
                      color={getRoleColor(admin.role)}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={admin.isActive ? t('admins.active') : t('admins.inactive')}
                      size="small"
                      color={admin.isActive ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatDate(admin.lastLogin)}</TableCell>
                  <TableCell>{formatDate(admin.createdAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditAdmin(admin)}
                        color="primary"
                        title={t('admins.edit')}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleChangePassword(admin)}
                        color="warning"
                        title={t('admins.changePassword')}
                      >
                        <Lock fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(admin)}
                        color="error"
                        disabled={isCurrentAdmin(admin)}
                        title={t('admins.delete')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={t('common.rowsPerPage')}
      />

      {formOpen && (
        <AdminForm
          open={formOpen}
          onClose={() => setFormOpen(false)}
          admin={selectedAdmin}
          onSuccess={handleFormSuccess}
          currentAdminId={currentAdmin?.id}
        />
      )}

      {passwordDialogOpen && (
        <ChangePasswordDialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          admin={passwordAdmin}
          onSuccess={handlePasswordSuccess}
        />
      )}

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, admin: null })}
      >
        <DialogTitle>{t('admins.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('admins.confirmDelete')}
          </DialogContentText>
          {deleteDialog.admin && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {deleteDialog.admin.username}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {deleteDialog.admin.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, admin: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admins;